import React, { useMemo, useState } from 'react';

import {
  Announcements,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ListOrdered,
  Share2,
} from 'lucide-react';

import MemberCourseCell from './MemberCourseCell';
import ScoreBadge from './ScoreBadge';
import { useDragScroll } from '../../hooks/useDragScroll';
import {
  useAddMyCoursePlayed,
  useMyCoursesPlayed,
  useRemoveMyCoursePlayed,
  useSetMyCoursesPlayedRank,
} from '../../hooks/useMemberCoursesPlayedApi';
import { freeLimitMessageFrom } from '../../hooks/useMyCourseFlags';
import {
  CourseMinifiedDTO,
  MemberCoursesPlayedSummaryDTO,
} from '../../services/apiClient';
import { showToast } from '../../utils/showToast';
import AddNewButton from '../common/AddButton';
import CheckboxField from '../common/formElements/CheckboxField';
import Table from '../common/Table';
import CoursePickerDrawer from '../drawers/CoursePickerDrawer';
import RankCourseDrawer from '../drawers/RankCourseDrawer';
import ShareCoursesPlayedDrawer from '../drawers/ShareCoursesPlayedDrawer';
import UpgradeDrawer from '../drawers/UpgradeDrawer';
import { DragHandle, SortableRow } from '../tables/SortableRow';

// The member's played courses as a personal ranking: favourite first, in the order they set by
// dragging a row by its handle (mouse, touch or keyboard, via dnd-kit), or with the up and down
// arrows from sm up. The order is saved (MemberCoursesPlayed.RankOrder). A new course arrives
// unranked: it waits in the "Unranked courses" strip above the list until the member places it
// with the ranking game (RankCourseDrawer), opened by Start ranking, Continue ranking, or a tap
// on one course in the strip. Each ranked course shows its score in a gold ring once the member
// has ranked 5. A drag carries a course into its new neighbours' Meh/Good/Great group, which the
// backend works out from movedCourseId. Sorting by name is only a view on top of it: the handles and arrows hide while it is on,
// and switching back shows the saved ranking again, untouched. A search does the same: moving a
// course while some are hidden would change places the member cannot see.
//
// One component, two homes: the desktop Courses Played page, and the Courses tab of the profile's
// golf lists (/member/profile/golf), which is the only way in on a phone.
//
// "Share list" opens ShareCoursesPlayedDrawer, which makes (or revokes) a public link to this list.
//
// Rating and the played date are gone, on the client's mark-up. Rating stays in the database,
// dormant (backlog R8); the date was only ever CreatedUtc, which reads "1 Jan 1" for migrated rows.
//
// The Played switch un-plays a course at once, with no confirm step. The row stays where it was,
// greyed, until the member leaves the list, so a mis-tap is undone by switching it back on. The
// course is re-added unranked and moves to the strip, to be placed again: the ranking around it
// may have changed, and its old place is not safe to assume.

type SortMode = 'rank' | 'az' | 'za';

const RANK_SORT_KEY = 'rank';
const NAME_SORT_KEY = 'name';

const MY_COURSES_PLAYED_KEY = ['myCoursesPlayed'];

// Table gives every cell px-4 and appends a column's className after it. On a phone that padding
// alone is a quarter of the card, so the columns that must stay reachable tighten it there. The
// `!` is needed because both are plain utilities, and the stylesheet decides which one wins, not
// the order they are written in.
const TIGHT_CELL = '!px-2 sm:!px-4';

// Lower-cased, per CLAUDE.md: GUID casing is not guaranteed across the member's rows, the picker's
// course data and the ids this page sends back.
const keyOf = (row: MemberCoursesPlayedSummaryDTO): string =>
  row.courseId?.toLowerCase() ?? '';

// "Western Cape, South Africa". The free-text address when the course has no place set.
const whereOf = (row: MemberCoursesPlayedSummaryDTO): string | undefined =>
  [row.stateName, row.countryName]
    .filter((part) => part != null && part.trim().length > 0)
    .join(', ') ||
  row.courseAddress ||
  undefined;

const matches = (row: MemberCoursesPlayedSummaryDTO, query: string): boolean =>
  [row.courseName, row.countryName, row.stateName].some((text) =>
    (text ?? '').toLowerCase().includes(query),
  );

const outlineButton =
  'inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-secondary bg-white px-4 text-sm font-semibold text-ink transition hover:bg-secondary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-white/30 dark:bg-transparent dark:text-white dark:hover:bg-white dark:hover:text-secondary';

const moveButton =
  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-gray-200 text-ink transition hover:border-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-30 dark:border-white/20 dark:text-white';

interface DisplayRow {
  // Table keys its rows by `id`.
  id: string;
  row: MemberCoursesPlayedSummaryDTO;
  // The row's place in the ranking, 1 first. Null for a course switched off on this visit.
  rank: number | null;
  isPlayed: boolean;
}

// A course switched off on this visit, and where it sat in the ranking when it was.
interface UnplayedRow {
  row: MemberCoursesPlayedSummaryDTO;
  index: number;
}

interface CoursesPlayedRankingProps {
  // Narrows the rows shown, by course, state or country name. Lower-cased and trimmed by the
  // caller. Empty shows them all.
  search?: string;
  // Pads the hint and buttons in from the edges, for a home with no padding of its own (the golf
  // lists). A Card already pads its content.
  inset?: boolean;
  // Only the courses in one place: a country or a state opened on the golf lists. The ranks stay
  // the member's real ones (South Africa can start at 3), and moving is off, as with a search.
  place?: { countryId?: string; stateId?: string; name: string };
}

const CoursesPlayedRanking: React.FC<CoursesPlayedRankingProps> = ({
  search = '',
  inset = false,
  place,
}) => {
  const queryClient = useQueryClient();
  const { data: coursesPlayed, isLoading } = useMyCoursesPlayed();
  const addCoursePlayed = useAddMyCoursePlayed();
  const removeCoursePlayed = useRemoveMyCoursePlayed();
  const setRank = useSetMyCoursesPlayedRank();

  const [sortMode, setSortMode] = useState<SortMode>('rank');
  const [unplayed, setUnplayed] = useState<UnplayedRow[]>([]);
  const [busyIds, setBusyIds] = useState<Set<string>>(() => new Set());
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState<string | null>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);
  // The ranking game. `rankStartId` is a course tapped in the strip; null starts at the first one.
  const [isRankOpen, setIsRankOpen] = useState(false);
  const [rankStartId, setRankStartId] = useState<string | null>(null);
  // Courses skipped in the ranking game on this visit. They stay in the strip, but the game passes
  // them by until the page is opened again.
  const [skippedIds, setSkippedIds] = useState<Set<string>>(() => new Set());
  const unrankedStrip = useDragScroll();

  // A pointer must travel a few pixels before a drag starts, so a click on the handle stays a
  // click. The keyboard sensor makes the handle work without a mouse: Space lifts the row, the
  // arrow keys move it, Space drops it, Escape puts it back.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const setBusy = (key: string, busy: boolean): void =>
    setBusyIds((current) => {
      const next = new Set(current);
      if (busy) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });

  // The ranking as shown: the saved list, with this visit's switched-off courses slotted back in
  // where they were. A switched-off course wins over the saved list, so a row does not flick back
  // to played in the moment between the switch and the refetch that drops it.
  const rankedRows = useMemo((): DisplayRow[] => {
    const unplayedKeys = new Set(unplayed.map((u) => keyOf(u.row)));

    const rows: DisplayRow[] = (coursesPlayed ?? [])
      .filter((row) => row.rankOrder != null && !unplayedKeys.has(keyOf(row)))
      .map((row, index) => ({
        id: keyOf(row),
        row,
        rank: index + 1,
        isPlayed: true,
      }));

    [...unplayed]
      .sort((a, b) => a.index - b.index)
      .forEach((u) =>
        rows.splice(Math.min(u.index, rows.length), 0, {
          id: keyOf(u.row),
          row: u.row,
          rank: null,
          isPlayed: false,
        }),
      );

    return rows;
  }, [coursesPlayed, unplayed]);

  const displayRows = useMemo((): DisplayRow[] => {
    const countryId = place?.countryId?.toLowerCase();
    const stateId = place?.stateId?.toLowerCase();
    const shown = rankedRows.filter(
      (d) =>
        (countryId == null || d.row.countryId?.toLowerCase() === countryId) &&
        (stateId == null || d.row.stateId?.toLowerCase() === stateId) &&
        (search.length === 0 || matches(d.row, search)),
    );

    if (sortMode === 'rank') {
      return shown;
    }

    const direction = sortMode === 'az' ? 1 : -1;
    return [...shown].sort(
      (a, b) =>
        direction *
        (a.row.courseName ?? '').localeCompare(
          b.row.courseName ?? '',
          undefined,
          {
            sensitivity: 'base',
          },
        ),
    );
  }, [rankedRows, sortMode, search, place?.countryId, place?.stateId]);

  // The courses still to rank, newest first (the order the backend sends them in). A course
  // switched off on this visit is not one of them.
  const unrankedRows = useMemo(() => {
    const unplayedKeys = new Set(unplayed.map((u) => keyOf(u.row)));
    return (coursesPlayed ?? []).filter(
      (row) => row.rankOrder == null && !unplayedKeys.has(keyOf(row)),
    );
  }, [coursesPlayed, unplayed]);

  // Every played course, ranked or not: the picker greys these out.
  const playedCourseIds = useMemo(
    () =>
      new Set([
        ...rankedRows.filter((d) => d.isPlayed).map((d) => d.id),
        ...unrankedRows.map(keyOf),
      ]),
    [rankedRows, unrankedRows],
  );

  const openRanking = (courseId: string | null): void => {
    setRankStartId(courseId);
    setIsRankOpen(true);
  };

  const skipCourse = (courseId: string): void =>
    setSkippedIds((current) => new Set(current).add(courseId));

  // The saved order, played courses only, as course ids for the rank endpoint.
  const playedOrder = (): string[] =>
    rankedRows
      .filter((d) => d.isPlayed && d.row.courseId != null)
      .map((d) => d.row.courseId as string);

  const handleMove = (display: DisplayRow, delta: -1 | 1): void => {
    const order = playedOrder();
    const from = order.findIndex((id) => id.toLowerCase() === display.id);
    const to = from + delta;

    if (from < 0 || to < 0 || to >= order.length) {
      return;
    }

    [order[from], order[to]] = [order[to], order[from]];

    setRank.mutate(
      { courseIds: order, movedCourseId: order[to] },
      {
        onError: (error: unknown) => {
          showToast('Could not save your ranking.', 'error');
          console.error('Could not save your ranking:', error);
        },
      },
    );
  };

  // Dropping a dragged row: move it within the list as shown (switched-off rows keep their slots,
  // so a course can be dropped either side of one), then save the played courses in that order.
  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (over == null || active.id === over.id) {
      return;
    }

    const from = rankedRows.findIndex((d) => d.id === active.id);
    const to = rankedRows.findIndex((d) => d.id === over.id);
    if (from < 0 || to < 0) {
      return;
    }

    const order = arrayMove(rankedRows, from, to)
      .filter((d) => d.isPlayed && d.row.courseId != null)
      .map((d) => d.row.courseId as string);

    const movedCourseId = rankedRows[from].row.courseId;
    setRank.mutate(
      { courseIds: order, movedCourseId },
      {
        onError: (error: unknown) => {
          showToast('Could not save your ranking.', 'error');
          console.error('Could not save your ranking:', error);
        },
      },
    );
  };

  // What a screen reader hears while dragging. dnd-kit's defaults read out the row ids, which here
  // are GUIDs; these say the course and the place instead.
  const nameOf = (id: UniqueIdentifier): string =>
    rankedRows.find((d) => d.id === id)?.row.courseName ?? 'Course';
  const placeOf = (id: UniqueIdentifier): number =>
    rankedRows.findIndex((d) => d.id === id) + 1;
  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      `Picked up ${nameOf(active.id)}, at position ${placeOf(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over != null
        ? `${nameOf(active.id)} moved to position ${placeOf(over.id)}.`
        : `${nameOf(active.id)} is no longer over the list.`,
    onDragEnd: ({ active, over }) =>
      over != null
        ? `${nameOf(active.id)} dropped at position ${placeOf(over.id)}.`
        : `${nameOf(active.id)} dropped.`,
    onDragCancel: ({ active }) =>
      `Moving ${nameOf(active.id)} was cancelled. It is back where it was.`,
  };

  const handleSort = (sortKey: string): void => {
    if (sortKey === RANK_SORT_KEY) {
      setSortMode('rank');
    } else {
      setSortMode((current) => (current === 'az' ? 'za' : 'az'));
    }
  };

  const reportAddFailure = (error: unknown): void => {
    // A free member who has used all their courses gets the upgrade offer, not an error, the same
    // answer the Explore map gives. The backend's 403 is the real gate; there is no browser-side
    // count here, so this list cannot disagree with it.
    const freeLimitMessage = freeLimitMessageFrom(error);
    if (freeLimitMessage != null) {
      setIsPickerOpen(false);
      setUpgradeMessage(freeLimitMessage);
    } else {
      showToast('Could not save that course as played.', 'error');
      console.error('Could not save that course as played:', error);
    }
  };

  const handleUnplay = async (display: DisplayRow): Promise<void> => {
    const courseId = display.row.courseId;
    if (courseId == null) {
      return;
    }

    const index = rankedRows.findIndex((d) => d.id === display.id);
    setUnplayed((current) => [...current, { row: display.row, index }]);
    setBusy(display.id, true);

    try {
      await removeCoursePlayed.mutateAsync(courseId);
    } catch (error: unknown) {
      setUnplayed((current) =>
        current.filter((u) => keyOf(u.row) !== display.id),
      );
      showToast('Could not update that course.', 'error');
      console.error('Could not un-play that course:', error);
    } finally {
      setBusy(display.id, false);
    }
  };

  // Switching a course back on re-adds it, unranked, so it moves to the strip to be placed again.
  // The row stays greyed until the list has been re-read with it in, so it never vanishes from the
  // list before it shows in the strip.
  const handleReplay = async (display: DisplayRow): Promise<void> => {
    const courseId = display.row.courseId;
    if (courseId == null) {
      return;
    }

    setBusy(display.id, true);

    try {
      await addCoursePlayed.mutateAsync(courseId);
      await queryClient.refetchQueries({ queryKey: MY_COURSES_PLAYED_KEY });
      setUnplayed((current) =>
        current.filter((u) => keyOf(u.row) !== display.id),
      );
    } catch (error: unknown) {
      reportAddFailure(error);
    } finally {
      setBusy(display.id, false);
    }
  };

  const handleAdd = async (course: CourseMinifiedDTO): Promise<void> => {
    if (course.id == null) {
      return;
    }

    try {
      await addCoursePlayed.mutateAsync(course.id);
      // A course switched off earlier on this visit and then added again from the picker is
      // played once more, so it must stop showing as switched off.
      const key = course.id.toLowerCase();
      setUnplayed((current) => current.filter((u) => keyOf(u.row) !== key));
      showToast(
        `${course.name ?? 'Course'} added to your unranked courses.`,
        'success',
      );
    } catch (error: unknown) {
      reportAddFailure(error);
      throw error;
    }
  };

  const isSearching = search.length > 0;
  const isRankView = sortMode === 'rank';
  // Moving needs the whole ranking on screen, in ranking order.
  const canMove = isRankView && !isSearching && place == null;
  const rankedCount = rankedRows.filter((d) => d.isPlayed).length;
  // The strip and its button sit with the whole list only, not inside one place or a search.
  const showUnranked = place == null && !isSearching && unrankedRows.length > 0;

  // Moving is paused while a save is in flight (a rank save, or a course being switched on or
  // off), so two quick moves cannot race each other and land in the wrong order.
  const isRankBusy = setRank.isPending || busyIds.size > 0;

  const hint =
    place != null
      ? `Your courses in ${place.name}, in your ranking order. Move them on the Courses tab.`
      : isSearching
        ? 'Showing the courses that match. Clear the search to move a course.'
        : isRankView
          ? 'Your ranking, favourite first. Drag a course by its handle to move it.'
          : `Sorted ${sortMode === 'az' ? 'A to Z' : 'Z to A'}. Your ranking is unchanged.`;

  return (
    <>
      <div
        className={`flex flex-wrap items-center justify-between gap-3 pb-3 ${
          inset ? 'px-4 pt-3' : ''
        }`}
      >
        <p className="min-w-0 flex-1 basis-56 text-sm text-ink-muted dark:text-white/60">
          {hint}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {!isRankView && (
            <button
              type="button"
              onClick={() => setSortMode('rank')}
              className={outlineButton}
            >
              <ListOrdered className="h-4 w-4" />
              My ranking
            </button>
          )}
          {/* Not inside one place: Share shares the whole list, and a course added here could
              land in another country and vanish from view. */}
          {place == null && (
            <>
              <button
                type="button"
                onClick={() => setIsShareOpen(true)}
                className={outlineButton}
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
              <AddNewButton label="Add" onClick={() => setIsPickerOpen(true)} />
            </>
          )}
        </div>
      </div>

      {showUnranked && (
        <div className={`pb-4 ${inset ? 'px-4' : ''}`}>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-muted dark:text-white/60">
            Unranked courses
            <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-secondary">
              {unrankedRows.length}
            </span>
          </p>
          {/* Swiped, not scrolled: no scrollbar (the Explore nearby strip's no-scrollbar), and
              each card snaps to the edge. On a phone a card is a little under half the width,
              so the next one peeks in and says there is more without a bar to say it. With the
              bar gone a mouse needs a handle, so the strip drags (useDragScroll). */}
          <div
            ref={unrankedStrip.ref}
            {...unrankedStrip.handlers}
            className={`no-scrollbar -mx-1 mt-2 flex scroll-px-1 gap-3 overflow-x-auto px-1 pb-2 ${
              unrankedStrip.isDragging
                ? 'cursor-grabbing snap-none select-none'
                : 'cursor-grab snap-x snap-mandatory'
            }`}
          >
            {unrankedRows.map((row) => (
              <button
                key={keyOf(row)}
                type="button"
                onClick={() => openRanking(keyOf(row))}
                className="flex w-[44%] shrink-0 snap-start flex-col sm:w-40 justify-between gap-3 rounded-xl border-2 border-dashed border-gray-300 p-3 text-left transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-white/20"
              >
                <span className="line-clamp-2 text-xs font-bold uppercase text-ink dark:text-white">
                  {row.courseName}
                </span>
                <span className="flex items-center justify-between text-xs text-ink-muted dark:text-white/50">
                  <span className="truncate">{whereOf(row)}</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                </span>
              </button>
            ))}
          </div>
          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={() => openRanking(null)}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-secondary transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {rankedCount > 0 ? 'Continue ranking' : 'Start ranking'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* A fallback, not the layout: the columns are sized to fit a phone, but a course name or a
          font size can still push them wider, and the card clips its overflow. Scrolling
          sideways beats a Played switch nobody can reach. */}
      <div className="overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
          accessibility={{ announcements }}
        >
          <SortableContext
            items={displayRows.map((d) => d.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              columns={[
                {
                  label: 'Rank',
                  key: RANK_SORT_KEY,
                  sortable: true,
                  className: TIGHT_CELL,
                  render: (display: DisplayRow): React.ReactElement => {
                    const livePosition = display.rank ?? 0;

                    return (
                      <div className="flex items-center gap-1 sm:gap-2">
                        {/* Renders nothing on a row that cannot move (SortableRow decides). */}
                        <DragHandle
                          label={`Drag ${display.row.courseName ?? 'this course'} to a new place`}
                        />

                        {/* The top three get a gold disc: the podium, in the brand's own colour
                            rather than a silver and bronze the palette does not have. */}
                        {display.isPlayed &&
                        display.rank != null &&
                        display.rank <= 3 &&
                        isRankView ? (
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-sm font-bold tabular-nums text-secondary">
                            {display.rank}
                          </span>
                        ) : (
                          <span
                            className={`w-5 text-right text-base font-semibold tabular-nums sm:w-7 ${
                              display.isPlayed
                                ? 'text-ink dark:text-white'
                                : 'text-ink-muted'
                            }`}
                          >
                            {display.rank ?? '-'}
                          </span>
                        )}

                        {/* From sm up only, and only when moving is allowed. On a phone the width
                            is needed for the course name, and the drag handle does the same job
                            under a thumb. Disabled while a save is in flight. */}
                        {canMove && display.isPlayed && (
                          <div className="hidden items-center gap-1 sm:flex">
                            <button
                              type="button"
                              onClick={() => handleMove(display, -1)}
                              disabled={isRankBusy || livePosition <= 1}
                              aria-label={`Move ${display.row.courseName ?? 'this course'} up`}
                              title="Move up"
                              className={moveButton}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMove(display, 1)}
                              disabled={
                                isRankBusy || livePosition >= rankedCount
                              }
                              aria-label={`Move ${display.row.courseName ?? 'this course'} down`}
                              title="Move down"
                              className={moveButton}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  },
                },
                {
                  label: 'Course',
                  key: NAME_SORT_KEY,
                  sortable: true,
                  className: TIGHT_CELL,
                  render: (display: DisplayRow) => (
                    <MemberCourseCell
                      courseId={display.row.courseId}
                      name={display.row.courseName}
                      subtitle={whereOf(display.row)}
                      logoFileId={display.row.courseLogoFileId}
                      closed={display.row.courseClosed}
                      muted={!display.isPlayed}
                      compact
                    />
                  ),
                },
                {
                  label: 'Pin',
                  key: 'pinName',
                  // Off on a phone: the Played switch has to fit beside the course, and a pin is
                  // set on few rows (most migrated ones have none). Back from sm up.
                  className: 'hidden sm:table-cell',
                  render: (display: DisplayRow) => (
                    <span className={display.isPlayed ? '' : 'opacity-50'}>
                      {display.row.pinName || '-'}
                    </span>
                  ),
                },
                {
                  label: 'Score',
                  key: 'score',
                  className: TIGHT_CELL,
                  render: (display: DisplayRow) => (
                    <span className={display.isPlayed ? '' : 'opacity-50'}>
                      <ScoreBadge
                        score={display.isPlayed ? display.row.score : null}
                      />
                    </span>
                  ),
                },
                {
                  label: 'Played',
                  key: 'played',
                  // The last column: a little more room on the right, so the switch is not
                  // against the edge of the screen on a phone.
                  className: '!pl-2 !pr-3 sm:!px-4',
                  render: (display: DisplayRow) => (
                    <CheckboxField
                      id={`played-${display.id}`}
                      value={display.isPlayed}
                      disabled={busyIds.has(display.id)}
                      haptic
                      onChange={(e) =>
                        void (e.target.checked
                          ? handleReplay(display)
                          : handleUnplay(display))
                      }
                    />
                  ),
                },
              ]}
              data={displayRows}
              isLoading={isLoading}
              allowPaging={false}
              expandableDetails={false}
              sortBy={isRankView ? RANK_SORT_KEY : NAME_SORT_KEY}
              sortDescending={sortMode === 'za'}
              onSort={handleSort}
              noDataMessage={
                isSearching
                  ? 'No course matches that.'
                  : unrankedRows.length > 0
                    ? 'Nothing ranked yet. Start ranking to build your list.'
                    : "You haven't logged any played courses yet."
              }
              renderRow={(display: DisplayRow, rowProps) => (
                <SortableRow
                  id={display.id}
                  // Only when the whole ranking is on screen, only a played course, not mid-save.
                  disabled={!canMove || !display.isPlayed || isRankBusy}
                  className={rowProps.className}
                >
                  {rowProps.children}
                </SortableRow>
              )}
            />
          </SortableContext>
        </DndContext>
      </div>

      <CoursePickerDrawer
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        title="Add a played course"
        selectedCourseIds={playedCourseIds}
        onAdd={handleAdd}
      />

      <RankCourseDrawer
        isOpen={isRankOpen}
        startCourseId={rankStartId}
        skippedIds={skippedIds}
        onSkip={skipCourse}
        onClose={() => setIsRankOpen(false)}
      />

      <ShareCoursesPlayedDrawer
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
      />

      <UpgradeDrawer
        isOpen={upgradeMessage != null}
        message={upgradeMessage}
        onClose={() => setUpgradeMessage(null)}
      />
    </>
  );
};

export default CoursesPlayedRanking;
